import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileText, Trash2, Settings, Briefcase, Code, Package, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const REQUIREMENT_TYPES = [
  { value: 'functional', label: 'Functional', icon: Settings },
  { value: 'technical', label: 'Technical', icon: Code },
  { value: 'business', label: 'Business', icon: Briefcase },
];

export function SolutionBuilder({ notes, aiRequirements, onAddNote, onDeleteNote, products }) {
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [requirementType, setRequirementType] = useState('functional');
  const [isAdding, setIsAdding] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);

  const safeNotes = notes || [];
  const safeAiRequirements = aiRequirements || [];
  const safeProducts = products || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category.trim() || !note.trim()) return;

    setIsAdding(true);
    try {
      await onAddNote({ category, note, requirement_type: requirementType });
      setCategory('');
      setNote('');
    } finally {
      setIsAdding(false);
    }
  };

  const getTypeIcon = (type) => {
    const found = REQUIREMENT_TYPES.find(t => t.value === type);
    return found ? found.icon : FileText;
  };

  const getTypeBadgeStyle = (type) => {
    switch (type) {
      case 'technical':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'business':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-brand-muted text-brand-primary border-brand-accent';
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="solution-builder">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-4">
        Solution Builder
      </span>

      {/* AI Detected Requirements */}
      {safeAiRequirements.length > 0 && (
        <div className="mb-4">
          <span className="text-xs font-medium text-zinc-500 mb-2 block">
            AI-Detected Requirements
          </span>
          <div className="space-y-2">
            {safeAiRequirements.map((req, index) => {
              const TypeIcon = getTypeIcon(req.category);
              return (
                <div 
                  key={index}
                  className="bg-brand-muted border border-brand-accent-muted rounded-md p-2 flex items-start gap-2"
                  data-testid={`ai-requirement-${index}`}
                >
                  <TypeIcon className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-brand-primary">{req.requirement}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Catalog */}
      {safeProducts.length > 0 && (
        <div className="mb-4" data-testid="products-catalog">
          <span className="text-xs font-medium text-zinc-500 mb-2 block">
            Product Catalog
          </span>
          <div className="space-y-2">
            {safeProducts.map((product) => {
              const isExpanded = expandedProduct === product.id;
              return (
                <div 
                  key={product.id}
                  className="border border-zinc-200 rounded-md bg-white overflow-hidden"
                  data-testid={`product-card-${product.id}`}
                >
                  <button
                    onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors text-left"
                    data-testid={`product-toggle-${product.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Package className="w-4 h-4 text-brand-primary flex-shrink-0" />
                      <span className="text-sm font-medium text-zinc-900 truncate">{product.name}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-3 border-t border-zinc-100">
                      <p className="text-xs text-zinc-600 mt-2">{product.description}</p>
                      
                      {product.features?.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-zinc-500 block mb-1">Features</span>
                          <div className="flex flex-wrap gap-1">
                            {product.features.map((feature, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {product.qualification_questions?.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-zinc-500 block mb-1">Qualifying Questions</span>
                          <div className="space-y-1">
                            {product.qualification_questions.map((q, i) => (
                              <div 
                                key={q.id || i} 
                                className="flex items-start gap-1.5 text-xs text-zinc-700 bg-amber-50 border border-amber-100 rounded p-1.5"
                                data-testid={`product-qual-question-${product.id}-${i}`}
                              >
                                <HelpCircle className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                                <span>{q.question}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Manual Notes */}
      <ScrollArea className="flex-1 mb-4">
        {safeNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="no-notes">
            <FileText className="w-8 h-8 text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">
              Capture solution requirements as they come up
            </p>
          </div>
        ) : (
          <div className="space-y-2 pr-2">
            {safeNotes.map((item, index) => {
              const TypeIcon = getTypeIcon(item.requirement_type);
              return (
                <div
                  key={item.id || index}
                  className="bg-white border border-zinc-200 rounded-md p-3 group"
                  data-testid={`solution-note-${index}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs bg-zinc-50">
                        {item.category}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getTypeBadgeStyle(item.requirement_type)}`}
                      >
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {item.requirement_type}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => onDeleteNote && onDeleteNote(item.id)}
                      data-testid={`delete-note-btn-${index}`}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                  <p className="text-sm text-zinc-700">{item.note}</p>
                  {item.timestamp && (
                    <span className="text-xs text-zinc-400 mt-1 block">
                      {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="border-t border-zinc-200 pt-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="text-sm rounded-md"
            data-testid="note-category-input"
          />
          <Select value={requirementType} onValueChange={setRequirementType}>
            <SelectTrigger className="rounded-md" data-testid="note-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REQUIREMENT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <type.icon className="w-3 h-3" />
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          placeholder="Capture a requirement or insight..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="text-sm rounded-md resize-none h-20"
          data-testid="note-text-input"
        />
        <Button 
          type="submit" 
          className="w-full bg-zinc-900 hover:bg-zinc-800 rounded-md"
          disabled={!category.trim() || !note.trim() || isAdding}
          data-testid="add-note-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {isAdding ? 'Adding...' : 'Add Note'}
        </Button>
      </form>
    </div>
  );
}
